import type { User } from '@supabase/supabase-js';
import { mapSupabaseUserToStatsig, type StatsigUser } from '../utils/statsigUser';

export const statsigConfig = {
  clientKey: import.meta.env.VITE_STATSIG_CLIENT_KEY,
  serverKey: import.meta.env.VITE_STATSIG_SERVER_KEY,
  environment: import.meta.env.MODE, // Use Vite's built-in MODE (development/production)
  
  // Modern configuration options
  options: {
    environment: {
      tier: import.meta.env.MODE // Use Vite's built-in MODE
    },
    disableCurrentVCLogging: false,
    // localMode prevents network requests in development to avoid polluting production analytics
    // Set to false temporarily if you need to test event tracking locally
    localMode: import.meta.env.MODE === 'development'
    // For testing events locally, temporarily change to:
    // localMode: false
    
    // 🧪 TESTING FEATURE FLAGS LOCALLY:
    // To test with live Statsig values during development, temporarily use:
    // localMode: false
    // Remember to change back to true when done testing!
  }
};

// Re-export from utils for convenience
export { mapSupabaseUserToStatsig, type StatsigUser } from '../utils/statsigUser';

// Device detection utility
export const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  const isTablet = /iPad|Android(?=.*\bMobile\b)(?=.*\bSafari\b)|Android(?=.*\bMobile\b)/i.test(userAgent) && window.innerWidth >= 768;
  
  let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  if (isMobile && !isTablet) deviceType = 'mobile';
  else if (isTablet) deviceType = 'tablet';

  // Basic browser detection
  let browser = 'unknown';
  if (userAgent.includes('Chrome')) browser = 'chrome';
  else if (userAgent.includes('Firefox')) browser = 'firefox';
  else if (userAgent.includes('Safari')) browser = 'safari';
  else if (userAgent.includes('Edge')) browser = 'edge';

  // Basic OS detection
  let os = 'unknown';
  if (userAgent.includes('Windows')) os = 'windows';
  else if (userAgent.includes('Mac')) os = 'macos';
  else if (userAgent.includes('Linux')) os = 'linux';
  else if (userAgent.includes('Android')) os = 'android';
  else if (userAgent.includes('iOS')) os = 'ios';

  return { type: deviceType, browser, os };
};

// Session Replay Configuration (for server-side analytics)
// Note: Client-side session replay plugins have been removed as part of privacy migration
export const sessionReplayConfig = {
  // Privacy settings - comprehensive masking for sensitive data
  maskAllInputs: true, // Mask all input fields by default
  maskAllText: false, // Don't mask all text (would make recordings useless)
  
  // Block sensitive elements using CSS classes and selectors
  blockClass: 'sr-block', // CSS class to completely block elements
  blockSelector: '[data-sensitive], [data-private], .sensitive-data, .private-info', // Block elements with these attributes/classes
  
  // Ignore certain elements from recording
  ignoreClass: 'sr-ignore', // CSS class to ignore elements
  
  // Detailed input masking configuration for different input types
  maskInputOptions: {
    color: false,        // Color pickers are safe
    date: false,         // Dates are generally safe
    'datetime-local': false,
    email: true,         // Mask email addresses
    month: false,
    number: false,       // Numbers might be safe depending on context
    range: false,        // Range sliders are safe
    search: false,       // Search terms might be okay
    tel: true,           // Mask phone numbers
    text: true,          // Mask text inputs (names, addresses, etc.)
    time: false,
    url: false,          // URLs might be okay
    week: false,
    textarea: true,      // Mask textarea content
    select: false,       // Select options are usually safe
    password: true       // Always mask passwords
  },
  
  // Network and iframe privacy
  recordCrossOriginIframes: false, // Don't record content from other domains
  
  // Performance and storage settings
  checkoutEveryNms: 10 * 1000, // Send data every 10 seconds
  checkoutEveryNth: 200,        // Or every 200 events
  
  // Additional privacy controls
  maskTextClass: 'sr-mask-text',     // Class to mask specific text elements
  maskTextSelector: '[data-mask-text]', // Selector for text masking
  
  // Ignore specific attributes that might contain sensitive data
  ignoreAttributes: ['data-user-id', 'data-email', 'data-phone']
};

// Plugin configuration (server-side only)
// Note: Client-side plugins have been removed as part of privacy migration to server-side analytics
export const getStatsigPlugins = () => {
  // Return empty array since we're using server-side analytics now
  return [];
};

// Server-side configuration for SSR
export const serverStatsigConfig = {
  serverKey: import.meta.env.VITE_STATSIG_SERVER_KEY,
  environment: import.meta.env.MODE,
  options: {
    environment: {
      tier: import.meta.env.MODE
    },
    localMode: import.meta.env.MODE === 'development',
    // Disable network requests in local mode
    disableNetworkRequests: import.meta.env.MODE === 'development'
  }
};

// Privacy compliance utilities
export const privacyUtils = {
  // CSS classes for privacy control
  BLOCK_CLASS: 'sr-block',
  IGNORE_CLASS: 'sr-ignore',
  MASK_TEXT_CLASS: 'sr-mask-text',
  
  // Data attributes for privacy control
  SENSITIVE_ATTR: 'data-sensitive',
  PRIVATE_ATTR: 'data-private',
  MASK_TEXT_ATTR: 'data-mask-text',
  
  // Helper function to mark elements as sensitive
  markAsSensitive: (element: HTMLElement) => {
    element.setAttribute('data-sensitive', 'true');
    element.classList.add('sr-block');
  },
  
  // Helper function to mark text for masking
  markTextForMasking: (element: HTMLElement) => {
    element.setAttribute('data-mask-text', 'true');
    element.classList.add('sr-mask-text');
  },
  
  // Helper function to ignore elements
  markAsIgnored: (element: HTMLElement) => {
    element.classList.add('sr-ignore');
  }
};

// Validation utilities
export const validateStatsigConfig = () => {
  const errors: string[] = [];
  
  if (!import.meta.env.VITE_STATSIG_CLIENT_KEY) {
    errors.push('VITE_STATSIG_CLIENT_KEY is required');
  }
  
  if (!import.meta.env.VITE_STATSIG_SERVER_KEY) {
    errors.push('VITE_STATSIG_SERVER_KEY is required');
  }
  
  if (import.meta.env.VITE_STATSIG_CLIENT_KEY && !import.meta.env.VITE_STATSIG_CLIENT_KEY.startsWith('client-')) {
    errors.push('VITE_STATSIG_CLIENT_KEY should start with "client-"');
  }
  
  if (import.meta.env.VITE_STATSIG_SERVER_KEY && !import.meta.env.VITE_STATSIG_SERVER_KEY.startsWith('secret-')) {
    errors.push('VITE_STATSIG_SERVER_KEY should start with "secret-"');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

// Validate configuration
if (!statsigConfig.clientKey) {
  console.warn('Statsig client key not found. Feature flags will not work.');
}

if (!statsigConfig.serverKey) {
  console.warn('Statsig server key not found. SSR feature flags will not work.');
}

// Export default configuration
export default {
  ...statsigConfig,
  plugins: getStatsigPlugins(),
  validation: validateStatsigConfig(),
  privacy: privacyUtils,
  sessionReplay: sessionReplayConfig
}; 